import React, { useState } from 'react';
import { CreateCustomerRequest, CustomerType, ServiceType } from '../../types';
import { useCustomerStore } from '../../stores/customerStore';
import { useProjectStore } from '../../stores/projectStore';
import './CustomerDialogs.css';

interface NewCustomerDialogProps {
  open: boolean;
  onClose: () => void;
}

const NewCustomerDialog: React.FC<NewCustomerDialogProps> = ({ open, onClose }) => {
  const { createCustomer } = useCustomerStore();
  const { currentProject } = useProjectStore();

  const [formData, setFormData] = useState<CreateCustomerRequest>({
    customer_type: 'individual',
    address: '',
    city: '',
    zip_code: '',
    email: '',
    phone: '',
    service_type: 'internet',
  });

  if (!open) return null;

  // Shared styles
  const labelStyle = { display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '6px' };
  const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' };
  const sectionStyle = { marginBottom: '24px' };
  const sectionTitleStyle = { fontSize: '18px', fontWeight: 600, color: '#1e293b', marginBottom: '16px' };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Zákazník patrí do projektu - bez neho by ho nikto nevidel
    if (!currentProject) {
      alert('Najprv vyberte projekt, do ktorého zákazník patrí.');
      return;
    }

    try {
      await createCustomer({ ...formData, project_id: currentProject.id });
      alert('Zákazník bol úspešne vytvorený');
      onClose();
      // Reset form
      setFormData({
        customer_type: 'individual',
        address: '',
        city: '',
        zip_code: '',
        email: '',
        phone: '',
        service_type: 'internet',
      });
    } catch (error) {
      alert('Chyba pri vytváraní zákazníka');
    }
  };

  const handleChange = (field: keyof CreateCustomerRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }} onClick={onClose}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        margin: '20px'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>➕ Nový zákazník</h2>
          <button onClick={onClose} style={{
            background: 'none',
            border: 'none',
            fontSize: '32px',
            cursor: 'pointer',
            color: '#64748b'
          }}>×</button>
        </div>

        <div style={{ padding: '24px' }}>
          <form onSubmit={handleSubmit}>
            {/* Customer Type */}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Typ zákazníka <span style={{ color: '#ef4444' }}>*</span></label>
              <select value={formData.customer_type} onChange={(e) => handleChange('customer_type', e.target.value as CustomerType)} required style={inputStyle}>
                <option value="individual">Fyzická osoba</option>
                <option value="company">Firma</option>
              </select>
            </div>

            {/* Personal/Company Info */}
            {formData.customer_type === 'individual' ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={labelStyle}>Meno <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="text" value={formData.first_name || ''} onChange={(e) => handleChange('first_name', e.target.value)} required style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Priezvisko <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="text" value={formData.last_name || ''} onChange={(e) => handleChange('last_name', e.target.value)} required style={inputStyle} />
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Názov firmy <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="text" value={formData.company_name || ''} onChange={(e) => handleChange('company_name', e.target.value)} required style={inputStyle} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={labelStyle}>IČO</label>
                    <input type="text" value={formData.ico || ''} onChange={(e) => handleChange('ico', e.target.value)} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>DIČ</label>
                    <input type="text" value={formData.dic || ''} onChange={(e) => handleChange('dic', e.target.value)} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>IČ DPH</label>
                    <input type="text" value={formData.dic_dph || ''} onChange={(e) => handleChange('dic_dph', e.target.value)} style={inputStyle} />
                  </div>
                </div>
              </div>
            )}

            {/* Address */}
            <div style={sectionStyle}>
              <h3 style={sectionTitleStyle}>Adresa</h3>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Ulica a číslo <span style={{ color: '#ef4444' }}>*</span></label>
                <input type="text" value={formData.address} onChange={(e) => handleChange('address', e.target.value)} required style={inputStyle} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={labelStyle}>Mesto <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="text" value={formData.city} onChange={(e) => handleChange('city', e.target.value)} required style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>PSČ <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="text" value={formData.zip_code} onChange={(e) => handleChange('zip_code', e.target.value)} required style={inputStyle} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={labelStyle}>GPS Latitude</label>
                  <input type="number" step="any" value={formData.latitude || ''} onChange={(e) => handleChange('latitude', e.target.value ? parseFloat(e.target.value) : undefined)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>GPS Longitude</label>
                  <input type="number" step="any" value={formData.longitude || ''} onChange={(e) => handleChange('longitude', e.target.value ? parseFloat(e.target.value) : undefined)} style={inputStyle} />
                </div>
              </div>
            </div>

            {/* Contact */}
            <div style={sectionStyle}>
              <h3 style={sectionTitleStyle}>Kontakt</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={labelStyle}>Email <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} required style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Telefón <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="tel" value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} required style={inputStyle} />
                </div>
              </div>
            </div>

            {/* Service */}
            <div style={sectionStyle}>
              <h3 style={sectionTitleStyle}>Služba</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={labelStyle}>Typ služby <span style={{ color: '#ef4444' }}>*</span></label>
                  <select value={formData.service_type} onChange={(e) => handleChange('service_type', e.target.value as ServiceType)} required style={inputStyle}>
                    <option value="internet">Internet</option>
                    <option value="internet_tv">Internet + TV</option>
                    <option value="internet_tv_phone">Internet + TV + Telefón</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Rýchlosť</label>
                  <input type="text" value={formData.service_speed || ''} onChange={(e) => handleChange('service_speed', e.target.value)} placeholder="napr. 100/100 Mbps" style={inputStyle} />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div style={sectionStyle}>
              <h3 style={sectionTitleStyle}>Poznámky</h3>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Poznámky zákazníka</label>
                <textarea value={formData.customer_notes || ''} onChange={(e) => handleChange('customer_notes', e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }} />
              </div>
              <div>
                <label style={labelStyle}>Interné poznámky</label>
                <textarea value={formData.internal_notes || ''} onChange={(e) => handleChange('internal_notes', e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }} />
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '10px 20px',
                  background: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500
                }}
              >
                Zrušiť
              </button>
              <button
                type="submit"
                style={{
                  padding: '10px 20px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500
                }}
              >
                Vytvoriť
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewCustomerDialog;

